import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TrendChart = ({ data, currency, chartType, onChartTypeChange, viewPeriod, isPro }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card p-12 rounded-lg shadow-elevation-3 border border-border">
          <p className="text-sm font-caption font-medium text-card-foreground mb-8">
            {label}
          </p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between mb-4">
              <span className="text-xs text-muted-foreground mr-12">{entry?.name}:</span>
              <span className="text-sm font-heading font-semibold" style={{ color: entry?.color }}>
                {currency}{entry?.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const getChartTitle = () => {
    if (viewPeriod === 'quarterly') return 'Quarterly Spending Trends';
    if (viewPeriod === 'yearly') return 'Year-to-Date Trends';
    return 'Monthly Spending Trends';
  };

  return (
    <div className="bg-card rounded-lg p-16 md:p-20 lg:p-24 shadow-elevation-2">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-16 gap-12">
        <div className="flex items-center">
          <Icon name="TrendingUp" size={24} color="var(--color-primary)" strokeWidth={2.5} />
          <h3 className="ml-12 text-lg md:text-xl lg:text-2xl font-heading font-semibold text-card-foreground">
            {getChartTitle()}
            {isPro && viewPeriod !== 'monthly' && (
              <span className="ml-8 text-xs px-8 py-4 rounded-full bg-primary/10 text-primary font-medium">Pro</span>
            )}
          </h3>
        </div>
        
        <div className="flex items-center gap-8">
          <Button
            variant={chartType === 'line' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChartTypeChange('line')}
            iconName="TrendingUp"
            iconSize={16}
          >
            Line
          </Button>
          <Button
            variant={chartType === 'bar' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChartTypeChange('bar')}
            iconName="BarChart3"
            iconSize={16}
          >
            Bar
          </Button>
        </div>
      </div>
      <div className="w-full h-[300px] sm:h-[350px] md:h-[400px]" aria-label="Spending Trend Chart">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'line' ? (
            <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="month" 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '11px', fontFamily: 'var(--font-caption)' }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '11px', fontFamily: 'var(--font-caption)' }}
                tickFormatter={(value) => `${currency}${(value / 1000)?.toFixed(0)}k`}
                width={60}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px', fontFamily: 'var(--font-caption)' }}
              />
              <Line 
                type="monotone" 
                dataKey="income" 
                stroke="var(--color-success)" 
                strokeWidth={2.5}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
              <Line 
                type="monotone" 
                dataKey="expense" 
                stroke="var(--color-error)" 
                strokeWidth={2.5}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          ) : (
            <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="month" 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '11px', fontFamily: 'var(--font-caption)' }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '11px', fontFamily: 'var(--font-caption)' }}
                tickFormatter={(value) => `${currency}${(value / 1000)?.toFixed(0)}k`}
                width={60}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px', fontFamily: 'var(--font-caption)' }}
              />
              <Bar dataKey="income" fill="var(--color-success)" radius={[8, 8, 0, 0]} />
              <Bar dataKey="expense" fill="var(--color-error)" radius={[8, 8, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TrendChart;